<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LoaderWithCheckmark from '@/components/LoaderWithCheckmark.vue';
import { supabase } from '@/plugins/supabase';
import { useAuthStore } from '@/store/auth';
import { getUserCountryCode } from '@/utilities/UserIPInfo';
import { trackNewSignup } from '@/utilities/tracking';
import { useIntercom } from '@/plugins/intercom/useIntercom';
import TransitionExpand from '@/components/TransitionExpand.vue';
import { trackSentryError } from '@/utilities/SentryHelper';
import { AuthErrorCodes } from '@/enums/ErrorCodes';

const router = useRouter();

const route = useRoute();

const authStore = useAuthStore();

const isSuccess = ref(false);

const { locale } = useI18n();

const intercom = useIntercom();

onMounted(async () => {
	const session = await supabase.auth.getSession();

	if (!session.data.session) {
		trackSentryError({
			errorCode: AuthErrorCodes.SUPABASE_OAUTH_LOGIN_ERROR,
			extend: {
				transactionName: 'OAuth Error: supabase.auth.getSession()',
				contexts: [
					{
						key: 'Supabase Login Error Context',
						value: {
							sessionError: session.error,
						},
					},
				],
			},
		});

		router.push({ name: 'signup' });
		return;
	}

	const countryCode = await getUserCountryCode() ?? undefined;

	const loginResult = await authStore.loginUserWithSupabase({
		token: session.data.session.access_token,
		locale: locale.value,
		countryCode,
	});

	if (loginResult.isErr()) {
		trackSentryError({
			errorCode: AuthErrorCodes.SUPABASE_OAUTH_LOGIN_ERROR,
			cause: loginResult.error,
			extend: {
				transactionName: 'OAuth Error: authStore.loginUserWithSupabase()',
				contexts: [
					{
						key: 'Supabase Login Error Context',
						value: {
							sessionError: session.error,
							countryCodeFromIpInfoIo: countryCode,
						},
					},
				],
			},
		});

		router.push({ name: 'signup' });
		return;
	}

	isSuccess.value = true;

	const { user: authUser, accountCreated } = loginResult.value;

	const authProvider = session.data.session.user.app_metadata.provider;

	if (accountCreated) {
		trackNewSignup(authProvider);
	}

	intercom.client.update({
		user_id: authUser.profileId,
		user_hash: authUser.intercomHash,
		name: `${authUser.firstname} ${authUser.lastname}`,
		email: authUser.email,
		beta: true,
	});

	router.push(route.query.redirectTo as string || { name: 'subscribe' });
});

</script>

<template>
	<div class="flex items-center justify-center flex-col h-screen max-w-xl mx-auto text-center">
		<LoaderWithCheckmark
			class="mb-4"
			:is-success="isSuccess"
		/>
		<TransitionExpand mode="out-in">
			<p v-if="isSuccess">
				{{ $t('view.socialCallback.title.pending') }}
			</p>
			<p v-else>
				{{ $t('view.socialCallback.title.redirect') }}
			</p>
		</TransitionExpand>
	</div>
</template>
